import { useRef, useCallback, useEffect } from 'react';

/**
 * 디바운스 훅 - 마지막 호출 후 일정 시간 대기 후 실행
 * @param handler 실행할 함수
 * @param delay 지연 시간 (ms)
 * @returns 디바운스된 함수
 */
const useDebounce = (handler: (...args: any[]) => void = () => {}, delay = 1000) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  return useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        handler(...args);
      }, delay);
    },
    [handler, delay],
  );
};

/**
 * 쓰로틀 훅 - 일정 시간 간격으로 최대 1번만 실행
 * @param handler 실행할 함수
 * @param delay 간격 시간 (ms)
 * @returns 쓰로틀된 함수
 */
const useThrottle = (handler: (...args: any[]) => void = () => {}, delay = 1000) => {
  const lastCallTime = useRef(0); // 마지막 호출 시간을 저장

  return useCallback(
    (...args: any[]) => {
      const now = Date.now();

      if (now - lastCallTime.current >= delay) {
        lastCallTime.current = now;
        handler(...args);
      }
    },
    [handler, delay],
  );
};

export const useClicker = (handler?: (...args: any[]) => void, delay?: number, mode?: 'throttle' | 'debounce') => {
  const throttle = useThrottle(handler, delay);
  const debounce = useDebounce(handler, delay);

  const clicker = (() => {
    if (!handler) return undefined;

    switch (mode) {
      case 'throttle':
        return throttle;
      case 'debounce':
        return debounce;
      default:
        return handler;
    }
  })();

  return clicker;
};

/**
 * 실제 활동 시간을 측정하는 훅
 * - 페이지가 보이지 않을 때(다른 탭, 최소화)는 시간을 측정하지 않음
 * - 사용자가 유휴 상태(10초간 활동 없음)일 때는 시간을 측정하지 않음
 *
 * @param isActive - 시간 측정 활성화 여부 (false면 측정 중지)
 * @returns getActiveTime - 현재까지 누적된 활동 시간(ms)을 반환하는 함수
 * @returns reset - 누적 시간을 0으로 초기화하는 함수
 */
export const useActiveTime = (isActive: boolean, idleTimeoutMs = 10_000) => {
  const activeTimeRef = useRef(0); // 누적된 활동 시간(ms)
  const lastInteractionTimeRef = useRef<number>(Date.now()); // 마지막 사용자 활동 시간
  const isIdleRef = useRef<boolean>(false); // 현재 유휴 상태 여부
  const rafIdRef = useRef<number | null>(null); // requestAnimationFrame ID
  const lastTickRef = useRef<number>(Date.now()); // 마지막 측정 시간

  useEffect(() => {
    // isActive가 false면 측정 중지
    if (!isActive) {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      return;
    }

    // 사용자 활동 감지 시 호출되는 함수
    const handleInteraction = () => {
      lastInteractionTimeRef.current = Date.now(); // 마지막 활동 시간 갱신
      isIdleRef.current = false; // 유휴 상태 해제
    };

    // 감지할 이벤트 목록 (마우스, 키보드, 터치, 스크롤 등)
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'touchmove', 'click'];
    events.forEach((event) => document.addEventListener(event, handleInteraction));

    // 매 프레임마다 실행되는 측정 함수
    const tick = () => {
      const now = Date.now();
      const timeSinceLastInteraction = now - lastInteractionTimeRef.current;

      // 마지막 활동 후 지정된 시간이 지나면 유휴 상태로 전환
      if (timeSinceLastInteraction > idleTimeoutMs) {
        isIdleRef.current = true;
      }

      // 페이지가 보이고(visible) + 유휴 상태가 아닐 때만 시간 누적
      if (document.visibilityState === 'visible' && !isIdleRef.current) {
        const delta = now - lastTickRef.current; // 이전 측정 이후 경과 시간
        // 비정상적으로 큰 시간 차이는 무시 (예: 컴퓨터 절전 모드)
        if (delta < 1000) {
          activeTimeRef.current += delta; // 시간 누적
        }
      }

      lastTickRef.current = now;
      rafIdRef.current = requestAnimationFrame(tick); // 다음 프레임에도 실행
    };

    // 초기화 및 측정 시작
    lastInteractionTimeRef.current = Date.now();
    lastTickRef.current = Date.now();
    rafIdRef.current = requestAnimationFrame(tick);

    // 컴포넌트 언마운트 시 정리
    return () => {
      events.forEach((event) => document.removeEventListener(event, handleInteraction));
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [isActive, idleTimeoutMs]);

  // 누적 시간 초기화 함수
  const reset = useCallback((resetInteraction = true) => {
    activeTimeRef.current = 0;
    // 주기 전송은 사용자 활동이 아니므로 유휴 판정 시각을 갱신하지 않는다.
    if (resetInteraction) {
      lastInteractionTimeRef.current = Date.now();
      isIdleRef.current = false;
    }
  }, []);

  // 현재까지 누적된 활동 시간(ms) 반환 함수
  const getActiveTime = useCallback(() => activeTimeRef.current, []);

  return { getActiveTime, reset };
};
